import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Article, Megaphone, Trash, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-border bg-card p-6" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-sans font-semibold">{label}</p>
          <p className="text-3xl font-serif font-medium text-foreground mt-1">{value}</p>
        </div>
        <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
          <Icon size={20} weight="regular" className="text-primary" />
        </div>
      </div>
    </div>
  );
}

function UsersTab({ users, onDelete }) {
  return (
    <div className="border border-border divide-y divide-border" data-testid="admin-users-table">
      <div className="grid grid-cols-12 gap-2 p-3 bg-muted/50 text-xs text-muted-foreground uppercase tracking-wide font-sans font-semibold">
        <div className="col-span-4">User</div>
        <div className="col-span-3">Email</div>
        <div className="col-span-2">Role</div>
        <div className="col-span-2">Joined</div>
        <div className="col-span-1"></div>
      </div>
      {users.map(u => (
        <div key={u.user_id} className="grid grid-cols-12 gap-2 p-3 items-center text-sm font-sans" data-testid={`admin-user-${u.user_id}`}>
          <div className="col-span-4 truncate font-medium">{u.name || 'Unnamed'}</div>
          <div className="col-span-3 truncate text-muted-foreground">{u.email}</div>
          <div className="col-span-2">
            <Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className="rounded-sm text-xs">
              {u.role}
            </Badge>
          </div>
          <div className="col-span-2 text-muted-foreground text-xs">
            {new Date(u.created_at).toLocaleDateString()}
          </div>
          <div className="col-span-1 text-right">
            {u.role !== 'admin' && (
              <button
                onClick={() => onDelete(u.user_id)}
                className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                data-testid={`admin-delete-user-${u.user_id}`}
              >
                <Trash size={16} />
              </button>
            )}
          </div>
        </div>
      ))}
      {!users.length && (
        <p className="p-6 text-center text-muted-foreground text-sm font-sans">No users found</p>
      )}
    </div>
  );
}

function PostsTab({ posts, onDelete }) {
  return (
    <div className="border border-border divide-y divide-border" data-testid="admin-posts-table">
      <div className="grid grid-cols-12 gap-2 p-3 bg-muted/50 text-xs text-muted-foreground uppercase tracking-wide font-sans font-semibold">
        <div className="col-span-3">User</div>
        <div className="col-span-4">Caption</div>
        <div className="col-span-2">Type</div>
        <div className="col-span-2">Date</div>
        <div className="col-span-1"></div>
      </div>
      {posts.map(p => (
        <div key={p.post_id} className="grid grid-cols-12 gap-2 p-3 items-center text-sm font-sans" data-testid={`admin-post-${p.post_id}`}>
          <div className="col-span-3 truncate font-medium">{p.user_name || 'Unknown'}</div>
          <div className="col-span-4 truncate text-muted-foreground">{p.caption || '(no caption)'}</div>
          <div className="col-span-2">
            <Badge variant="outline" className="rounded-sm text-xs">{p.media_type}</Badge>
          </div>
          <div className="col-span-2 text-muted-foreground text-xs">
            {new Date(p.created_at).toLocaleDateString()}
          </div>
          <div className="col-span-1 text-right">
            <button
              onClick={() => onDelete(p.post_id)}
              className="p-1 text-muted-foreground hover:text-destructive transition-colors"
              data-testid={`admin-delete-post-${p.post_id}`}
            >
              <Trash size={16} />
            </button>
          </div>
        </div>
      ))}
      {!posts.length && (
        <p className="p-6 text-center text-muted-foreground text-sm font-sans">No posts found</p>
      )}
    </div>
  );
}

function AdsTab({ ads, onDelete, onCreate }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', image_url: '', target_url: '', level: 1, placement: 'feed', active: true });
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async () => {
    if (!form.title || !form.image_url || !form.target_url) { toast.error('Fill all required fields'); return; }
    setSubmitting(true);
    try {
      await onCreate({ ...form, level: parseInt(form.level) });
      setForm({ title: '', image_url: '', target_url: '', level: 1, placement: 'feed', active: true });
      setShowForm(false);
    } catch {} finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-ads-section">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowForm(!showForm)}
          className="rounded-sm text-xs gap-1"
          data-testid="admin-create-ad-btn"
        >
          Create Ad
        </Button>
      </div>

      {showForm && (
        <div className="border border-border p-4 space-y-3 bg-muted/30 rounded-sm" data-testid="admin-ad-form">
          <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Ad title"
            className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans" data-testid="admin-ad-title" />
          <input value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} placeholder="Image URL"
            className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans" data-testid="admin-ad-image" />
          <input value={form.target_url} onChange={e => setForm(p => ({ ...p, target_url: e.target.value }))} placeholder="Target URL"
            className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans" data-testid="admin-ad-target" />
          <div className="flex gap-4">
            <select value={form.level} onChange={e => setForm(p => ({ ...p, level: e.target.value }))}
              className="bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none text-sm font-sans py-2" data-testid="admin-ad-level">
              <option value={1}>Level 1</option><option value={2}>Level 2</option><option value={3}>Level 3</option>
            </select>
            <select value={form.placement} onChange={e => setForm(p => ({ ...p, placement: e.target.value }))}
              className="bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none text-sm font-sans py-2" data-testid="admin-ad-placement">
              <option value="feed">Feed</option><option value="sidebar">Sidebar</option><option value="banner">Banner</option>
            </select>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)} className="rounded-sm">Cancel</Button>
            <Button size="sm" onClick={handleCreate} disabled={submitting} className="rounded-sm bg-primary" data-testid="admin-ad-save">Create</Button>
          </div>
        </div>
      )}

      <div className="border border-border divide-y divide-border">
        {ads.map(ad => (
          <div key={ad.ad_id} className="flex items-center justify-between p-3" data-testid={`admin-ad-${ad.ad_id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium font-sans truncate">{ad.title}</p>
              <p className="text-xs text-muted-foreground font-sans">{ad.placement} | Level {ad.level} | {ad.active ? 'Active' : 'Inactive'}</p>
            </div>
            <button onClick={() => onDelete(ad.ad_id)} className="p-1 text-muted-foreground hover:text-destructive transition-colors ml-2" data-testid={`admin-delete-ad-${ad.ad_id}`}>
              <Trash size={16} />
            </button>
          </div>
        ))}
        {!ads.length && <p className="p-6 text-center text-muted-foreground text-sm font-sans">No ads created</p>}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [stats, setStats] = useState({ users_count: 0, posts_count: 0, ads_count: 0 });
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, postsRes, adsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users?limit=100'),
        api.get('/admin/posts?limit=100'),
        api.get('/admin/ads'),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setPosts(postsRes.data.posts);
      setAds(adsRes.data);
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!user) return <Navigate to="/auth" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.user_id !== userId));
      setStats(s => ({ ...s, users_count: s.users_count - 1 }));
      toast.success('User deleted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleDeletePost = async (postId) => {
    try {
      await api.delete(`/posts/${postId}`);
      setPosts(prev => prev.filter(p => p.post_id !== postId));
      setStats(s => ({ ...s, posts_count: s.posts_count - 1 }));
      toast.success('Post deleted');
    } catch (err) { toast.error('Delete failed'); }
  };

  const handleCreateAd = async (adData) => {
    const { data } = await api.post('/admin/ads', adData);
    setAds(prev => [data, ...prev]);
    setStats(s => ({ ...s, ads_count: s.ads_count + 1 }));
    toast.success('Ad created');
  };

  const handleDeleteAd = async (adId) => {
    try {
      await api.delete(`/admin/ads/${adId}`);
      setAds(prev => prev.filter(a => a.ad_id !== adId));
      setStats(s => ({ ...s, ads_count: s.ads_count - 1 }));
      toast.success('Ad deleted');
    } catch { toast.error('Delete failed'); }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background">
        <div className="animate-pulse w-8 h-8 rounded-full bg-muted" />
      </div>
    );
  }

  const tabs = [
    { key: 'users', label: 'Users', icon: Users },
    { key: 'posts', label: 'Posts', icon: Article },
    { key: 'ads', label: 'Ads', icon: Megaphone },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background" data-testid="admin-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="font-serif text-3xl sm:text-4xl text-foreground font-medium tracking-tight" data-testid="admin-title">
          Admin Dashboard
        </h1>
        <p className="text-muted-foreground text-sm mt-2 font-sans">Manage your platform</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-px mt-8 bg-border" data-testid="admin-stats">
          <StatCard icon={Users} label="Users" value={stats.users_count} />
          <StatCard icon={Article} label="Posts" value={stats.posts_count} />
          <StatCard icon={Megaphone} label="Ads" value={stats.ads_count} />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mt-10" data-testid="admin-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-6 pb-3 text-xs font-semibold tracking-wide uppercase font-sans transition-colors ${
                tab === t.key ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="mt-6">
          {tab === 'users' && <UsersTab users={users} onDelete={handleDeleteUser} />}
          {tab === 'posts' && <PostsTab posts={posts} onDelete={handleDeletePost} />}
          {tab === 'ads' && <AdsTab ads={ads} onDelete={handleDeleteAd} onCreate={handleCreateAd} />}
        </div>
      </div>
    </div>
  );
}
